/**
 * The Studio template's catalogs: the typefaces a trainer may pick, the 3D objects, and the colour
 * presets. Every id here is one the backend allow-lists in the stored appearance document
 * (`public-form-requirements.md` R1), so an id is renamed only together with the server.
 *
 * ── Why the heading fonts carry metrics ──────────────────────────────────────────────────────
 * The trainer's name is set as large as the screen allows, and how large that is depends on how
 * wide the face draws a letter. The four numbers per face are transcribed from the owner-approved
 * welcome mockups (`V3-Tel-Powitanie`, `V3-Desk-Powitanie`): the average advance of an uppercase
 * letter as a fraction of the font size, the tracking, the weight the face is drawn in and its
 * line height. A condensed face (Anton, Bebas Neue) therefore sets the same name bigger than a wide
 * one (Montserrat), which is what the mockups draw.
 *
 * The faces added on 28 September 2026 take Poppins' and Space Grotesk's numbers from the same
 * mockups' font table. The other eight were not in it: their letter width is the browser's measured
 * average uppercase advance at their weight times 1.15 — the ratio between the measurement and the
 * transcribed number for the grotesques that have both (Bricolage, Jakarta, Montserrat, Poppins,
 * Space Grotesk) — so they are sized by the same yardstick as the canon's.
 *
 * Plus Jakarta Sans is the app's own face and its files are the app's own (`public/fonts/`); every
 * other family is self-hosted under `public/fonts/public-form/` (R1: never from a font service).
 */

export type HeadingFontId =
  | 'bricolage'
  | 'jakarta'
  | 'anton'
  | 'bebas'
  | 'oswald'
  | 'montserrat'
  | 'instrument-serif'
  | 'playfair'
  | 'roboto'
  | 'open-sans'
  | 'poppins'
  | 'raleway'
  | 'nunito'
  | 'work-sans'
  | 'space-grotesk'
  | 'archivo'
  | 'barlow'
  | 'outfit';

export type BodyFontId =
  | 'jakarta'
  | 'inter'
  | 'dm-sans'
  | 'manrope'
  | 'lato'
  | 'montserrat'
  | 'roboto'
  | 'open-sans'
  | 'poppins'
  | 'raleway'
  | 'nunito'
  | 'work-sans'
  | 'space-grotesk'
  | 'archivo'
  | 'barlow'
  | 'outfit'
  | 'bricolage';

export interface HeadingFont {
  readonly id: HeadingFontId;
  /** The CSS family name, which is also the name the builder shows — a typeface's name is not translated. */
  readonly family: string;
  /** Average uppercase advance as a fraction of the font size. */
  readonly letterWidth: number;
  readonly tracking: string;
  readonly weight: number;
  readonly leading: number;
}

export interface BodyFont {
  readonly id: BodyFontId;
  readonly family: string;
}

export const HEADING_FONTS: readonly HeadingFont[] = [
  { id: 'bricolage', family: 'Bricolage Grotesque', letterWidth: 0.7, tracking: '-0.055em', weight: 800, leading: 0.84 },
  { id: 'jakarta', family: 'Plus Jakarta Sans', letterWidth: 0.74, tracking: '-0.05em', weight: 800, leading: 0.86 },
  { id: 'anton', family: 'Anton', letterWidth: 0.47, tracking: '-0.01em', weight: 400, leading: 0.9 },
  { id: 'bebas', family: 'Bebas Neue', letterWidth: 0.4, tracking: '0em', weight: 400, leading: 0.9 },
  { id: 'oswald', family: 'Oswald', letterWidth: 0.5, tracking: '-0.01em', weight: 700, leading: 0.9 },
  { id: 'montserrat', family: 'Montserrat', letterWidth: 0.82, tracking: '-0.05em', weight: 800, leading: 0.88 },
  { id: 'instrument-serif', family: 'Instrument Serif', letterWidth: 0.56, tracking: '-0.03em', weight: 400, leading: 0.86 },
  { id: 'playfair', family: 'Playfair Display', letterWidth: 0.7, tracking: '-0.03em', weight: 800, leading: 0.88 },
  { id: 'poppins', family: 'Poppins', letterWidth: 0.8, tracking: '-0.05em', weight: 800, leading: 0.9 },
  { id: 'space-grotesk', family: 'Space Grotesk', letterWidth: 0.72, tracking: '-0.05em', weight: 700, leading: 0.86 },
  { id: 'roboto', family: 'Roboto', letterWidth: 0.74, tracking: '-0.04em', weight: 800, leading: 0.88 },
  { id: 'open-sans', family: 'Open Sans', letterWidth: 0.81, tracking: '-0.04em', weight: 800, leading: 0.9 },
  { id: 'raleway', family: 'Raleway', letterWidth: 0.77, tracking: '-0.04em', weight: 800, leading: 0.88 },
  { id: 'nunito', family: 'Nunito', letterWidth: 0.8, tracking: '-0.04em', weight: 800, leading: 0.9 },
  { id: 'work-sans', family: 'Work Sans', letterWidth: 0.78, tracking: '-0.04em', weight: 800, leading: 0.88 },
  { id: 'archivo', family: 'Archivo', letterWidth: 0.82, tracking: '-0.04em', weight: 800, leading: 0.88 },
  { id: 'barlow', family: 'Barlow', letterWidth: 0.71, tracking: '-0.03em', weight: 800, leading: 0.88 },
  { id: 'outfit', family: 'Outfit', letterWidth: 0.78, tracking: '-0.04em', weight: 800, leading: 0.88 },
];

export const BODY_FONTS: readonly BodyFont[] = [
  { id: 'jakarta', family: 'Plus Jakarta Sans' },
  { id: 'inter', family: 'Inter' },
  { id: 'dm-sans', family: 'DM Sans' },
  { id: 'manrope', family: 'Manrope' },
  { id: 'lato', family: 'Lato' },
  { id: 'montserrat', family: 'Montserrat' },
  { id: 'roboto', family: 'Roboto' },
  { id: 'open-sans', family: 'Open Sans' },
  { id: 'poppins', family: 'Poppins' },
  { id: 'raleway', family: 'Raleway' },
  { id: 'nunito', family: 'Nunito' },
  { id: 'work-sans', family: 'Work Sans' },
  { id: 'space-grotesk', family: 'Space Grotesk' },
  { id: 'archivo', family: 'Archivo' },
  { id: 'barlow', family: 'Barlow' },
  { id: 'outfit', family: 'Outfit' },
  { id: 'bricolage', family: 'Bricolage Grotesque' },
];

/**
 * The weights the body is set in (`--fw-regular` to `--fw-extrabold`), which the page loads before it
 * draws. A face that ships fewer answers each with its nearest file (`tokens/studio.css`).
 */
export const BODY_WEIGHTS: readonly number[] = [400, 600, 700, 800];

/* ── Type size ──────────────────────────────────────────────────────────────────────────────── */

/**
 * How large each face is set, chosen apart for the headings and the body (owner request of
 * 3 October 2026). `m` is the template as the mockups draw it and multiplies every size by exactly
 * 1, so a form saved before the choice existed draws pixel for pixel as it did. The other two move
 * every size of their family by one factor; the headings move further than the body because they
 * start larger and carry the page. A size that is fitted to its frame (the name, a typed number)
 * takes the factor at its cap only: fitting wins, so a long name never runs off the frame.
 */
export type StudioFontSize = 's' | 'm' | 'l';

export const STUDIO_FONT_SIZES: readonly StudioFontSize[] = ['s', 'm', 'l'];

export const DEFAULT_FONT_SIZE: StudioFontSize = 'm';

const HEADING_SCALE: Readonly<Record<StudioFontSize, number>> = { s: 0.88, m: 1, l: 1.14 };
const BODY_SCALE: Readonly<Record<StudioFontSize, number>> = { s: 0.92, m: 1, l: 1.1 };

/** The factor every heading size is multiplied by. */
export function headingScale(size: StudioFontSize): number {
  return HEADING_SCALE[size];
}

/** The factor every body size is multiplied by. */
export function bodyScale(size: StudioFontSize): number {
  return BODY_SCALE[size];
}

export const DEFAULT_HEADING_FONT: HeadingFontId = 'bricolage';
export const DEFAULT_BODY_FONT: BodyFontId = 'jakarta';

export function headingFont(id: HeadingFontId): HeadingFont {
  return HEADING_FONTS.find((font) => font.id === id) ?? firstOf(HEADING_FONTS);
}

export function bodyFont(id: BodyFontId): BodyFont {
  return BODY_FONTS.find((font) => font.id === id) ?? firstOf(BODY_FONTS);
}

/* ── 3D objects ─────────────────────────────────────────────────────────────────────────────── */

export type StudioObjectId =
  | 'kettlebell'
  | 'dumbbell'
  | 'plate'
  | 'ball'
  | 'barbell'
  | 'ropes'
  | 'gloves'
  | 'sandbag';

export const STUDIO_OBJECT_IDS: readonly StudioObjectId[] = [
  'kettlebell',
  'dumbbell',
  'plate',
  'ball',
  'barbell',
  'ropes',
  'gloves',
  'sandbag',
];

/**
 * Every library object is one matte orange image on a transparent ground, drawn opaque over whatever
 * stands behind it (the portrait included). It is recoloured by rotating its hue, and this is the
 * hue it was rendered in — the rotation for a colour is that colour's hue minus this.
 */
export const OBJECT_BASE_HUE = 16;

/** Where a library object's image is served from. A static asset of this app, never the API. */
export function studioObjectSrc(id: StudioObjectId): string {
  return `/public-form/objects/obj-${id}.webp`;
}

/* ── Example photographs ─────────────────────────────────────────────────────────────────────── */

/**
 * MoveBuddy's own example photographs, drawn where a trainer has not uploaded theirs: the portrait
 * stands on the page as a cut-out, the avatar beside the message. The document and the page's read
 * name them by id (`coach-portrait`, `coach-avatar`); the files are static assets of this app, never
 * the API. An id this build does not ship draws nothing.
 */
const SAMPLE_SRC: Readonly<Record<string, string>> = {
  'coach-portrait': '/public-form/samples/coach-portrait.webp',
  'coach-avatar': '/public-form/samples/coach-avatar.webp',
};

export function studioSampleSrc(id: string | null | undefined): string | null {
  return id !== null && id !== undefined && Object.hasOwn(SAMPLE_SRC, id) ? (SAMPLE_SRC[id] ?? null) : null;
}

function firstOf<T>(list: readonly T[]): T {
  const first = list[0];
  if (first === undefined) throw new Error('a Studio catalog is empty');
  return first;
}
